import { Routes } from '@angular/router';

import { AdminLoginPageComponent } from './admin/pages/login/admin-login-page.component';
import type { SeoRouteData } from './core/seo/seo.types';
import { guestPlatformBlockedGuard } from './interview/auth/guards/guest-platform-blocked.guard';
import { tenantPortalRoutes } from './tenant/tenant-portal.routes';
import { tenantSlugAppRoutes } from './tenant/tenant-slug-app.routes';

const loginSeo: SeoRouteData = {
  title: 'Sign in',
  description:
    'Sign in to Akalan with Google to practice AI mock interviews, view feedback reports, and manage your credits.',
  robots: 'noindex, nofollow',
};

export const routes: Routes = [
  {
    path: 'session',
    loadChildren: () =>
      import('./interview/interview/interview-session.routes').then((m) => m.interviewSessionRoutes),
  },
  {
    path: 'admin/login',
    data: {
      seo: {
        title: 'Admin sign in',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    component: AdminLoginPageComponent,
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin/admin.routes').then((m) => m.adminRoutes),
  },
  {
    path: 'portal',
    children: tenantPortalRoutes,
  },
  {
    path: 'invites/accept/:token',
    data: { seo: { title: 'Accept invitation', robots: 'noindex, nofollow' } satisfies SeoRouteData },
    loadComponent: () =>
      import('./tenant/pages/invite-accept/tenant-invite-accept-page.component').then((m) => m.TenantInviteAcceptPageComponent),
  },
  {
    path: 'candidate-invite/:token',
    data: {
      inviteMode: 'personal',
      seo: { title: 'Interview invitation', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./tenant/pages/candidate-invite/candidate-apply-landing-page.component').then(
        (m) => m.CandidateApplyLandingPageComponent,
      ),
  },
  {
    path: 'apply/:token',
    data: {
      inviteMode: 'open',
      seo: { title: 'Apply for role', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./tenant/pages/candidate-invite/candidate-apply-landing-page.component').then(
        (m) => m.CandidateApplyLandingPageComponent,
      ),
  },
  {
    path: 'launch/:token',
    data: { seo: { title: 'Join interview', robots: 'noindex, nofollow' } satisfies SeoRouteData },
    loadComponent: () =>
      import('./interview/launch/pages/launch-page.component').then((m) => m.LaunchPageComponent),
  },
  {
    path: 'interview/complete',
    data: { seo: { title: 'Interview complete', robots: 'noindex, nofollow' } satisfies SeoRouteData },
    loadComponent: () =>
      import('./interview/interview/pages/complete/interview-complete-page.component').then(
        (m) => m.InterviewCompletePageComponent,
      ),
  },
  {
    path: 'interview/:token',
    data: {
      inviteMode: 'link',
      seo: { title: 'Start interview', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./tenant/pages/interview-link/interview-link-landing-page.component').then(
        (m) => m.InterviewLinkLandingPageComponent,
      ),
  },
  {
    path: 'college',
    data: {
      seo: { title: 'College portal', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadChildren: () =>
      import('./college/college.routes').then((m) => m.collegeRoutes),
  },
  {
    path: 'company',
    data: {
      seo: { title: 'Company portal', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadChildren: () =>
      import('./company/company.routes').then((m) => m.companyRoutes),
  },
  {
    path: 'student',
    data: {
      seo: { title: 'Student portal', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadChildren: () =>
      import('./student/student.routes').then((m) => m.studentRoutes),
  },
  {
    path: 'login',
    data: { seo: loginSeo },
    loadComponent: () =>
      import('./interview/auth/pages/login-page/login-page.component').then((m) => m.LoginPageComponent),
  },
  {
    path: 'forgot-password',
    data: {
      seo: {
        title: 'Forgot password',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/auth/pages/forgot-password/forgot-password-page.component').then(
        (m) => m.ForgotPasswordPageComponent,
      ),
  },
  {
    path: 'reset-password',
    data: {
      seo: {
        title: 'Reset password',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/auth/pages/reset-password/reset-password-page.component').then(
        (m) => m.ResetPasswordPageComponent,
      ),
  },
  {
    path: 'auth/google/done',
    data: {
      seo: {
        title: 'Signing in',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/auth/pages/google-oauth-done-page.component').then(
        (m) => m.GoogleOauthDonePageComponent,
      ),
  },
  {
    path: 'join/:code',
    data: {
      seo: { title: 'Join with a friend’s link', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/auth/pages/join/join-referral-page.component').then((m) => m.JoinReferralPageComponent),
  },
  {
    path: 'a/:code',
    data: {
      seo: { title: 'Akalan', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./features/affiliate/pages/affiliate-landing-page.component').then(
        (m) => m.AffiliateLandingPageComponent,
      ),
  },
  {
    path: 'affiliate',
    data: {
      seo: { title: 'Affiliate portal', robots: 'noindex, nofollow' } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./features/affiliate/pages/affiliate-portal-page.component').then(
        (m) => m.AffiliatePortalPageComponent,
      ),
  },
  {
    path: 'signup',
    data: {
      seo: {
        title: 'Create account',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/auth/pages/signup/signup-redirect.component').then((m) => m.SignupRedirectComponent),
  },
  {
    path: 'onboarding',
    pathMatch: 'full',
    redirectTo: '/onboarding/1',
  },
  {
    path: 'onboarding/:step',
    canActivate: [guestPlatformBlockedGuard],
    data: {
      seo: {
        title: 'Your interview profile',
        description:
          'Tell Akalan your role, experience, and language so we can tailor AI interview questions and feedback.',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/onboarding/pages/onboarding-page.component').then((m) => m.OnboardingPageComponent),
  },
  {
    path: 'blog',
    loadChildren: () => import('./features/blog/blog.routes').then((m) => m.blogRoutes),
  },
  {
    path: 'blog/portal',
    pathMatch: 'prefix',
    redirectTo: '/blog',
  },
  {
    path: 'p/:slug',
    data: {
      seo: {
        title: 'Portfolio',
        description: 'Personal portfolio published with Akalan.',
        robots: 'index, follow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./features/portfolio/pages/public/public-portfolio-page.component').then(
        (m) => m.PublicPortfolioPageComponent,
      ),
  },
  ...tenantSlugAppRoutes,
  {
    path: 'report/pdf-print/:sessionId',
    data: {
      seo: {
        title: 'Report PDF',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/interview/report/pages/pdf-print/report-pdf-print-page.component').then(
        (m) => m.ReportPdfPrintPageComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: '',
        loadChildren: () => import('./interview/home/home.routes').then((m) => m.homeRoutes),
      },
      {
        path: 'dashboard',
        data: {
          seo: {
            robots: 'noindex, nofollow',
            description:
              'Your Akalan dashboard: start mock interviews, review scores and feedback, and manage your account.',
          } satisfies SeoRouteData,
        },
        loadChildren: () => import('./interview/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
      },
      {
        path: 'report/share/:sessionId',
        data: {
          seo: {
            title: 'Shared interview report',
            description: 'Read-only interview practice report shared via Akalan.',
            robots: 'noindex, nofollow',
          } satisfies SeoRouteData,
        },
        loadComponent: () =>
          import('./interview/interview/report/pages/public/public-report-page.component').then((m) => m.PublicReportPageComponent),
      },
      {
        path: 'interview/room',
        pathMatch: 'full',
        data: {
          seo: {
            title: 'Interview room',
            robots: 'noindex, nofollow',
          } satisfies SeoRouteData,
        },
        loadComponent: () =>
          import('./interview/not-found/pages/not-found-page.component').then((m) => m.NotFoundPageComponent),
      },
    ],
  },
  {
    path: '**',
    data: {
      seo: {
        title: 'Page not found',
        description: 'This Akalan page could not be found.',
        robots: 'noindex, nofollow',
      } satisfies SeoRouteData,
    },
    loadComponent: () =>
      import('./interview/not-found/pages/not-found-page.component').then((m) => m.NotFoundPageComponent),
  },
];
